import { useState, useRef, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';

interface SkillOption {
  id: number;
  title: string;
  domainId: number;
  domainTitle: string;
}

interface SkillsGroupedSelectProps {
  skills: SkillOption[];
  value: number | null;
  onChange: (id: number | null) => void;
}

export default function SkillsGroupedSelect({ skills, value, onChange }: SkillsGroupedSelectProps) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  const grouped = skills.reduce<Record<string, SkillOption[]>>((acc, skill) => {
    if (!acc[skill.domainTitle]) acc[skill.domainTitle] = [];
    acc[skill.domainTitle].push(skill);
    return acc;
  }, {});

  const selectedTitle = value != null ? skills.find((s) => s.id === value)?.title : null;

  const select = (id: number | null) => {
    onChange(id);
    setOpen(false);
  };

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`w-full flex items-center justify-between px-2 py-1.5 text-xs bg-white border rounded-md transition-colors focus:outline-none ${
          open ? 'border-pine-pine text-pine-ink' : 'border-pine-line text-pine-ink hover:border-pine-pine'
        }`}
      >
        <span className={`truncate min-w-0 ${selectedTitle ? 'text-pine-ink' : 'text-pine-ink-soft'}`}>
          {selectedTitle ?? 'All skills'}
        </span>
        <ChevronDown className={`w-3 h-3 text-pine-ink-soft flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute z-10 w-full mt-1 border-[1.5px] border-pine-line rounded-[9px] bg-white shadow-[0_4px_12px_rgba(0,0,0,0.08)] max-h-[200px] overflow-y-auto">
          <button
            type="button"
            onClick={() => select(null)}
            className={`w-full text-left px-3 py-2 text-[13px] transition-colors ${
              value === null
                ? 'bg-pine-hover-item text-pine-pine font-medium'
                : 'hover:bg-[#F4F9F6] text-pine-ink-soft'
            }`}
          >
            All skills
          </button>
          {Object.entries(grouped).map(([domain, domainSkills]) => (
            <div key={domain}>
              <p className="px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.08em] text-pine-ink-soft bg-[#F4F9F6]">
                {domain}
              </p>
              {domainSkills.map((skill) => (
                <button
                  key={skill.id}
                  type="button"
                  onClick={() => select(skill.id)}
                  className={`w-full text-left px-5 py-2 text-[13px] transition-colors ${
                    value === skill.id
                      ? 'bg-pine-hover-item text-pine-pine font-medium'
                      : 'hover:bg-[#F4F9F6] text-pine-ink'
                  }`}
                >
                  {skill.title}
                </button>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
